import { useState } from 'react';

function CodeBlock({ code, language = 'json5' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group my-4">
      <div className="flex items-center justify-between bg-slate-800 rounded-t-lg px-4 py-2 border border-slate-700 border-b-0">
        <span className="text-xs text-slate-400 font-mono">{language}</span>
        <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">
          <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
          {copied ? '已复制' : '复制'}
        </button>
      </div>
      <pre className="bg-slate-950 border border-slate-700 rounded-b-lg p-4 overflow-x-auto">
        <code className="text-sm text-slate-300 font-mono leading-relaxed">{code}</code>
      </pre>
    </div>
  );
}

function InfoBox({ type = 'info', children }: { type?: 'info' | 'warning' | 'tip'; children: React.ReactNode }) {
  const styles = {
    info: { bg: 'bg-cyan-500/5 border-cyan-500/20', icon: 'ri-information-line', iconColor: 'text-cyan-400' },
    warning: { bg: 'bg-amber-500/5 border-amber-500/20', icon: 'ri-alert-line', iconColor: 'text-amber-400' },
    tip: { bg: 'bg-emerald-500/5 border-emerald-500/20', icon: 'ri-lightbulb-line', iconColor: 'text-emerald-400' },
  };
  const s = styles[type];
  return (
    <div className={`${s.bg} border rounded-lg p-4 my-4`}>
      <div className="flex items-start gap-3">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className={`${s.icon} ${s.iconColor} text-lg`}></i>
        </div>
        <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function SectionTitle({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-xl font-bold text-white mt-10 mb-4 flex items-center gap-2 scroll-mt-24">
      <span className="w-1 h-5 bg-cyan-400 rounded-full inline-block"></span>
      {children}
    </h2>
  );
}

function SubTitle({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <h3 id={id} className="text-base font-semibold text-slate-200 mt-6 mb-3 scroll-mt-24">{children}</h3>
  );
}

export default function SkillsContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">技能（Skills）</h1>
        <p className="text-slate-400 text-sm leading-relaxed">
          Skills 是 OpenClaw 的能力扩展系统，通过 Markdown 文件为智能体注入额外的指令、知识和行为规则。
          Skills 可以从多个位置加载，支持优先级覆盖和条件门控。
        </p>
      </div>

      <SectionTitle id="sk-locations">加载位置与优先级</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        Skills 从三个位置加载，优先级从高到低依次为：
      </p>
      <div className="space-y-3 my-4">
        {[
          {
            priority: '1',
            label: '智能体专属 Skills',
            path: '~/.openclaw/agents/<agent-name>/skills/',
            desc: '仅对特定智能体生效，优先级最高，可覆盖共享 Skills 中的同名文件。',
            color: 'border-cyan-500/30 bg-cyan-500/5',
            badge: 'text-cyan-400',
          },
          {
            priority: '2',
            label: '共享 Skills',
            path: '~/.openclaw/skills/',
            desc: '对所有智能体共享，适合放置通用规则和知识。',
            color: 'border-amber-500/30 bg-amber-500/5',
            badge: 'text-amber-400',
          },
          {
            priority: '3',
            label: '工作区 Skills',
            path: '<workspace>/skills/',
            desc: '随工作区分发，适合项目级别的技能配置。优先级最低。',
            color: 'border-slate-600/50 bg-slate-800/30',
            badge: 'text-slate-400',
          },
        ].map((loc) => (
          <div key={loc.priority} className={`border rounded-lg p-4 ${loc.color}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`text-xs font-bold font-mono ${loc.badge}`}>优先级 {loc.priority}</span>
              <span className="text-sm font-semibold text-white">{loc.label}</span>
            </div>
            <code className="text-xs text-slate-400 font-mono block mb-1.5">{loc.path}</code>
            <p className="text-sm text-slate-300">{loc.desc}</p>
          </div>
        ))}
      </div>
      <InfoBox type="tip">
        同名 Skill 文件，高优先级位置的版本会完全覆盖低优先级的版本。
        利用这一机制，可以为特定智能体定制覆盖共享 Skill 的行为。
      </InfoBox>

      <SectionTitle id="sk-single-vs-shared">单智能体 vs 共享 Skills</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-robot-line text-cyan-400"></i>
            <span className="text-sm font-semibold text-white">单智能体 Skills</span>
          </div>
          <p className="text-sm text-slate-300 mb-3">放置在 <code className="text-cyan-400 bg-slate-700 px-1 rounded text-xs">agents/&lt;name&gt;/skills/</code> 下，只对该智能体生效。</p>
          <ul className="space-y-1.5 text-xs text-slate-400">
            <li className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-400"></i>适合个性化行为定制</li>
            <li className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-400"></i>可覆盖共享 Skills</li>
            <li className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-400"></i>不影响其他智能体</li>
          </ul>
        </div>
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-share-line text-amber-400"></i>
            <span className="text-sm font-semibold text-white">共享 Skills</span>
          </div>
          <p className="text-sm text-slate-300 mb-3">放置在 <code className="text-amber-400 bg-slate-700 px-1 rounded text-xs">~/.openclaw/skills/</code> 下，所有智能体共享。</p>
          <ul className="space-y-1.5 text-xs text-slate-400">
            <li className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-400"></i>适合通用规则和知识</li>
            <li className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-400"></i>统一维护，一处修改全局生效</li>
            <li className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-400"></i>可被单智能体 Skills 覆盖</li>
          </ul>
        </div>
      </div>

      <SectionTitle id="sk-clawhub">ClawHub 安装与同步</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        ClawHub 是 OpenClaw 的 Skills 市场，提供社区贡献的 Skills 包，可以一键安装：
      </p>
      <CodeBlock language="bash" code={`# 从 ClawHub 安装 Skill
openclaw skills install @clawhub/coding-assistant

# 列出已安装的 Skills
openclaw skills list

# 更新所有 Skills
openclaw skills update

# 卸载 Skill
openclaw skills uninstall @clawhub/coding-assistant

# 搜索 ClawHub
openclaw skills search "python"`} />
      <InfoBox type="info">
        ClawHub Skills 安装后会同步到 <code className="text-cyan-300 bg-slate-800 px-1 py-0.5 rounded text-xs">~/.openclaw/skills/</code> 目录，
        对所有智能体生效。如需仅对特定智能体生效，请手动复制到对应的智能体 Skills 目录。
      </InfoBox>

      <SectionTitle id="sk-format">SKILL.md 格式</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        每个 Skill 是一个 Markdown 文件，文件头部使用 YAML frontmatter 定义元数据：
      </p>
      <CodeBlock language="markdown" code={`---
name: python-expert
description: Python 编程专家技能
version: 1.0.0
# 门控条件：满足条件才加载此 Skill
gates:
  - tool: exec          # 需要 exec 工具可用
  - model: anthropic    # 仅在使用 Anthropic 模型时加载
# 环境变量注入
env:
  PYTHON_VERSION: "3.11"
  MAX_RETRIES: "3"
# 优先级（数字越大越先加载）
priority: 10
---

# Python 专家

你是一位 Python 编程专家，擅长：

- 编写高质量、符合 PEP 8 规范的 Python 代码
- 使用类型注解提高代码可读性
- 编写完善的单元测试
- 性能优化和代码审查

## 编码规范

1. 始终使用类型注解
2. 函数文档字符串使用 Google 风格
3. 优先使用列表推导式而非 for 循环
4. 异常处理要具体，避免裸 except`} />

      <SectionTitle id="sk-gates">门控（加载时过滤）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        通过 frontmatter 中的 <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">gates</code> 字段，
        可以设置 Skill 的加载条件，只有满足条件时才会注入该 Skill：
      </p>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium w-1/3">门控类型</th>
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">说明</th>
            </tr>
          </thead>
          <tbody>
            {[
              { key: 'tool: exec', desc: '当 exec 工具可用时加载' },
              { key: 'model: anthropic', desc: '当使用 Anthropic 模型时加载' },
              { key: 'model: openai', desc: '当使用 OpenAI 模型时加载' },
              { key: 'channel: telegram', desc: '当通过 Telegram 渠道交互时加载' },
              { key: 'agent: my-agent', desc: '仅对指定智能体加载' },
              { key: 'env: MY_VAR', desc: '当环境变量 MY_VAR 存在时加载' },
            ].map((row, i) => (
              <tr key={i} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors">
                <td className="py-2.5 px-3"><code className="text-cyan-400 font-mono text-xs">{row.key}</code></td>
                <td className="py-2.5 px-3 text-slate-300">{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CodeBlock language="markdown" code={`---
name: shell-expert
gates:
  # 多个条件为 AND 关系，全部满足才加载
  - tool: exec
  - tool: process
  - model: anthropic
---

# Shell 脚本专家
...`} />

      <SectionTitle id="sk-config-override">配置覆盖</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        在 <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">openclaw.json</code> 中可以对 Skills 进行全局配置覆盖：
      </p>
      <CodeBlock language="json5" code={`{
  agents: [
    {
      name: "my-agent",
      skills: {
        // 禁用特定 Skill
        disabled: ["python-expert", "@clawhub/coding-assistant"],
        // 强制启用（忽略门控条件）
        forceEnabled: ["shell-expert"],
        // 覆盖 Skill 的 frontmatter 配置
        overrides: {
          "python-expert": {
            priority: 20,
            env: {
              PYTHON_VERSION: "3.12",
            }
          }
        }
      }
    }
  ]
}`} />

      <SectionTitle id="sk-env">环境变量注入</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        Skills 可以通过 frontmatter 的 <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">env</code> 字段注入环境变量，
        这些变量会在智能体运行时可用：
      </p>
      <CodeBlock language="markdown" code={`---
name: api-client
env:
  API_BASE_URL: "https://api.example.com"
  API_VERSION: "v2"
  REQUEST_TIMEOUT: "30"
---

# API 客户端

使用以下环境变量连接 API：
- 基础 URL：\${API_BASE_URL}
- API 版本：\${API_VERSION}
- 超时时间：\${REQUEST_TIMEOUT} 秒`} />
      <InfoBox type="tip">
        环境变量注入是向 Skill 传递配置的推荐方式，避免在 Skill 文件中硬编码敏感信息。
        可以在 <code className="text-emerald-300 bg-slate-800 px-1 py-0.5 rounded text-xs">openclaw.json</code> 的 <code className="text-emerald-300 bg-slate-800 px-1 py-0.5 rounded text-xs">skills.overrides</code> 中覆盖这些值。
      </InfoBox>
    </div>
  );
}
